import Input from 'components/Input/Input'
import { Form, Formik, Field, FormikState } from 'formik'
import * as yup from 'yup'
import { useState } from 'react'
import { Box, Typography, useMediaQuery, useTheme } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import { useCountries } from 'hooks/useCountries'
import { usePlace, usePatchPlace } from 'hooks/usePlaces'
import { Country } from 'types/model/common'
import { getCountries, isValidCoordinate } from 'lib/utils'
import { SelectDropdown } from 'components/SelectDropdown/SelectDropdown'
import { SaveBtn } from 'components/SaveBtn/SaveBtn'
import { useAlert } from 'providers/AlertProvider'
import GoBackButton from 'components/GoBackButton/GoBackButton'
import LoadingIndicator from 'components/LoadingIndicator/LoadingIndicator'
import { ResponsiveBoxWrapper } from 'components/Layout/Layout.styled'
import { BlueSwitch } from './Place.styled'
import { PlaceSecondarySidebar } from './PlaceSecondarySidebar'

type PlaceFormProps = {
  searchable: boolean
  name: string
  retailerId: string
  currencyCode: string
  street: string
  building: string
  city: string
  postcode: string
  country: string
  latitude: string
  longitude: string
}

export const PlaceInfo = () => {
  const { t } = useTranslation()
  const { placeId } = useParams()
  const { isLoading, error, place } = usePlace(placeId as string)
  const { loading, countries } = useCountries()
  const { mutateAsync, isLoading: isLoadingRequest } = usePatchPlace(
    placeId as string
  )

  const { setAlert } = useAlert()
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))

  const [showOverlay, setShowOverlay] = useState(true)

  const initialValues = {
    searchable: place.searchable,
    name: place.name,
    retailerId: place.extId,
    currencyCode: place.currencyCode,
    street: place.street,
    building: place.building,
    city: place.city,
    postcode: place.postcode,
    country: place.country.code,
    latitude: place.latitude.toString(),
    longitude: place.longitude.toString(),
  }

  const validationSchema = yup.object({
    searchable: yup.boolean().required(t('required')),
    name: yup
      .string()
      .required(t('required'))
      .min(2, t('atLeastTwoCharacters')),
    retailerId: yup.string().required(t('required')),
    currencyCode: yup.string().required(t('required')),
    street: yup
      .string()
      .required(t('required'))
      .min(2, t('atLeastTwoCharacters')),
    building: yup.string(),
    city: yup
      .string()
      .required(t('required'))
      .min(2, t('atLeastTwoCharacters')),
    postcode: yup.string().required(t('required')),
    country: yup.string().required(t('required')),
    latitude: yup
      .string()
      .required(t('required'))
      .test('isLatitude', t('validLatitude'), (num) =>
        isValidCoordinate(num as string, 'latitude')
      ),
    longitude: yup
      .string()
      .required(t('required'))
      .test('isLongitude', t('validLongitude'), (num) =>
        isValidCoordinate(num as string, 'longitude')
      ),
  })

  const handleSubmit = async (
    values: PlaceFormProps,
    resetForm: (nextState?: Partial<FormikState<PlaceFormProps>>) => void
  ) => {
    const country = countries.filter(
      (country: Country) => country.code === values.country
    )[0]
    const latitude = parseFloat(values.latitude)
    const longitude = parseFloat(values.longitude)
    const modifiedValues = {
      searchable: values.searchable,
      name: values.name,
      street: values.street,
      building: values.building,
      city: values.city,
      postcode: values.postcode,
      currency_code: values.currencyCode,
      country,
      latitude,
      longitude,
    }
    await mutateAsync(modifiedValues, {
      onSuccess: () => {
        setAlert(t('saved'), 'success', {
          vertical: 'bottom',
          horizontal: isMobile ? 'center' : 'left',
        })
        resetForm({ values })
      },
      onError: () => {
        setAlert(t('failureMessage'), 'failure', {
          vertical: 'bottom',
          horizontal: isMobile ? 'center' : 'left',
        })
      },
    })
  }

  if (isLoading || !place || loading) return <LoadingIndicator />
  if (error) return <h1>Error</h1>
  return (
    <Box sx={{ display: 'flex' }}>
      <PlaceSecondarySidebar
        placeId={placeId as string}
        placeName={place.name}
        placeMasterId={place.masterId}
      />
      <ResponsiveBoxWrapper showOverlay={showOverlay}>
        <Box maxWidth='500px'>
          {isMobile && <GoBackButton onClick={() => setShowOverlay(false)} />}
          <Formik
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={async (values, { resetForm }) =>
              handleSubmit(values, resetForm)
            }>
            {({ setFieldValue, values, isValid, errors, dirty }) => (
              <Form>
                <Box
                  display='flex'
                  flexDirection='column'
                  justifyContent='space-between'>
                  {place && (
                    <Box
                      display='flex'
                      flexDirection='column'
                      justifyContent='space-around'
                      mt={theme.spacing(3)}>
                      <Box
                        display='flex'
                        justifyContent='space-between'
                        alignItems='center'
                        mb={theme.spacing(3)}>
                        <Typography variant='body2'>
                          {t('storeVisibleToConsumers')}
                        </Typography>
                        <Field
                          name='searchable'
                          component={BlueSwitch}
                          value={values.searchable}
                          checked={values.searchable}
                          onChange={() => {
                            setFieldValue('searchable', !values.searchable)
                          }}
                          aria-label={t('toggleVisibleToConsumers')}
                        />
                      </Box>
                      <Input
                        errorMessage={errors.name as string | undefined}
                        label={t('name')}
                        name='name'
                        type='text'
                        variant='filled'
                        fullWidth
                      />
                      <Input
                        disabled
                        label={t('retailerId')}
                        name='retailerId'
                        type='text'
                        variant='filled'
                        fullWidth
                      />
                      <Input
                        label={t('currencyInputLabel')}
                        name='currencyCode'
                        type='text'
                        variant='filled'
                        fullWidth
                      />
                      <Typography
                        variant='body2'
                        sx={{
                          marginBottom: theme.spacing(3),
                          marginTop: 0,
                        }}>
                        {t('address')}
                      </Typography>
                      <Box
                        sx={{
                          display: isMobile ? 'block' : 'flex',
                          gap: theme.spacing(2),
                        }}
                        justifyContent='space-between'>
                        <Input
                          errorMessage={errors.street as string | undefined}
                          label={t('street')}
                          name='street'
                          type='text'
                          variant='filled'
                          flexBasis='70%'
                        />
                        <Input
                          errorMessage={errors.building as string | undefined}
                          label={t('building')}
                          name='building'
                          type='text'
                          variant='filled'
                          flexBasis='30%'
                        />
                      </Box>
                      <Box
                        sx={{
                          display: isMobile ? 'block' : 'flex',
                          gap: theme.spacing(2),
                        }}
                        justifyContent='space-between'>
                        <Input
                          errorMessage={errors.postcode as string | undefined}
                          label={t('postCode')}
                          name='postcode'
                          type='text'
                          variant='filled'
                          flexBasis='30%'
                        />
                        <Input
                          errorMessage={errors.city as string | undefined}
                          label={t('city')}
                          name='city'
                          type='text'
                          variant='filled'
                          flexBasis='70%'
                        />
                      </Box>
                      <Field
                        errorMessage={errors.country as string | undefined}
                        type='select'
                        component={SelectDropdown}
                        isMobile={isMobile}
                        name='country'
                        selectedValue={values.country}
                        selectOptions={getCountries(countries)}
                        handleChange={(value: string) => {
                          setFieldValue('country', value)
                        }}
                        label={t('country')}
                      />
                      <Typography
                        variant='body2'
                        sx={{
                          marginBottom: theme.spacing(3),
                        }}>
                        {t('location')}
                      </Typography>
                      <Box
                        mb={theme.spacing(4)}
                        sx={{
                          display: isMobile ? 'block' : 'flex',
                          gap: theme.spacing(2),
                        }}
                        justifyContent='space-between'>
                        <Input
                          errorMessage={errors.latitude as string | undefined}
                          label={t('latitude')}
                          name='latitude'
                          type='text'
                          variant='filled'
                          flexBasis='50%'
                        />
                        <Input
                          errorMessage={errors.longitude as string | undefined}
                          label={t('longitude')}
                          name='longitude'
                          type='text'
                          variant='filled'
                          flexBasis='50%'
                        />
                      </Box>
                    </Box>
                  )}
                </Box>
                <Box
                  sx={{
                    position: 'fixed',
                    bottom: isMobile ? '0px' : theme.spacing(2),
                    width: isMobile ? '92%' : '504px',
                    zIndex: 1000,
                  }}>
                  <SaveBtn
                    showOverlay={showOverlay}
                    dirty={dirty}
                    isValid={isValid}
                    isLoadingRequest={isLoadingRequest}
                  />
                </Box>
              </Form>
            )}
          </Formik>
        </Box>
      </ResponsiveBoxWrapper>
    </Box>
  )
}
